import React from "react";
import {
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { fill, useCopy } from "../copy";
import { FLOAT_SHADOW, LOOK } from "../look";
import { AppViewport, WalletScreen } from "../ui/AppScreens";
import { STORY_GAIN } from "../data/brands";
import { StepStage, useStepGeom } from "../ui/StepStage";
import { useStage } from "../stage";
import { Sfx } from "../../b2b/ui/Sfx";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Step 3 — your friend buys, you both win. Frak blue. The coded wallet
 * (live brands in the history), a push notification (copy from the
 * landing's notification asset), the balance ticking up by the story
 * brand's reward as the purchase slides into the history, then who got what.
 */

// Notification holds ~2s (title + 15-word body), then the balance rolls
// and the two chips land with ~1.5s of hold before the cut.
const NOTIF_IN = 12;
const NOTIF_OUT = 72;
const ROLL_FROM = 72;
const ROLL_TO = 88;
const CHIP_FRIEND = 86;
const CHIP_YOU = 92;

/** Chip top in wallet points (square/tall): centred on the balance. */
const CHIP_TOP_PT = 126;

const BALANCE_FROM = 16;
const BALANCE_TO = BALANCE_FROM + STORY_GAIN;

const Chip: React.FC<{
  at: number;
  label: string;
  value: string;
  emoji: string;
  accent: string;
  side: "left" | "right";
}> = ({ at, label, value, emoji, accent, side }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const stage = useStage();
  const g = useStepGeom();
  // Square/tall: pinned to the frame edges, overlapping the phone. Wide:
  // straddling the phone's edges (the frame edges are far away).
  const wide = stage.shape === "wide";
  // Tall: the phone is ~1.5× bigger, so its balance sits closer to the
  // frame edge; a slightly smaller chip keeps clear of "21,00".
  const s = stage.shape === "tall" ? 0.88 : 1;
  const x: React.CSSProperties =
    side === "left"
      ? wide
        ? { right: stage.w - (g.phoneX + 100) }
        : { left: 36 }
      : wide
        ? { left: g.phoneX + g.phoneW - 100 }
        : { right: 36 };
  const p = spring({ frame: frame - at, fps, config: { damping: 12, stiffness: 220, mass: 0.6 } });
  return (
    <div
      style={{
        position: "absolute",
        // Wide: the friend chip drops below the step label (left column).
        // Square/tall: level with the balance, flanking "21,00" in the empty
        // sides of its card, so no chip covers the purchase row sliding in.
        top: wide && side === "left" ? g.phoneY + g.phoneH * 0.74 : g.phoneY + CHIP_TOP_PT * g.k,
        ...x,
        padding: `${24 * s}px ${32 * s}px ${24 * s}px ${26 * s}px`,
        whiteSpace: "nowrap",
        borderRadius: 36 * s,
        background: LOOK.white,
        boxShadow: FLOAT_SHADOW,
        display: "flex",
        alignItems: "center",
        gap: 20 * s,
        scale: p,
        rotate: `${side === "left" ? -4 : 4}deg`,
        transformOrigin: side === "left" ? "100% 50%" : "0% 50%",
      }}
    >
      <div style={{ fontSize: 64 * s, lineHeight: 1 }}>{emoji}</div>
      <div style={{ lineHeight: 1.05 }}>
        <div style={{ fontFamily: LOOK.body, fontWeight: LOOK.medium, fontSize: 30 * s, color: LOOK.inkSoft }}>
          {label}
        </div>
        <div
          style={{
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
            fontSize: 46 * s,
            letterSpacing: -1.5 * s,
            color: accent,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

export const Earn: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const g = useStepGeom();
  // Notification: 780 wide, centred on the phone, just below its top.
  const NOTIF_W = 780;
  const notifTop = g.phoneY + 12;

  const notifIn = spring({
    frame: frame - NOTIF_IN,
    fps,
    config: { damping: 16, stiffness: 200, mass: 0.7 },
  });
  const notifOut = interpolate(frame, [NOTIF_OUT, NOTIF_OUT + 8], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.7, 0, 0.84, 0),
  });
  const notifY =
    interpolate(notifIn, [0, 1], [-(notifTop + 310), 0]) - notifOut * (notifTop + 370);

  const value = interpolate(frame, [ROLL_FROM, ROLL_TO], [BALANCE_FROM, BALANCE_TO], {
    ...clamp,
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  // The purchase lands in the history as the balance rolls.
  const newRow = interpolate(frame, [ROLL_FROM, ROLL_FROM + 12], [0, 1], {
    ...clamp,
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const flash = interpolate(frame, [ROLL_FROM, ROLL_FROM + 4, ROLL_TO + 10, ROLL_TO + 24], [0, 1, 1, 0], clamp);

  return (
    <StepStage
      background={LOOK.blue}
      step={3}
      label={copy.steps.earn}
      labelStart={0}
      dark
      screen={
        <>
          <AppViewport screenWidth={g.SW}>
            <WalletScreen balance={value} flash={flash} newRow={newRow} gain={STORY_GAIN} />
          </AppViewport>
        </>
      }
    >
      <Chip
        at={CHIP_FRIEND}
        side="left"
        emoji="🎁"
        label={copy.steps.friendLabel}
        value={copy.steps.friendValue}
        accent={LOOK.blue}
      />
      <Chip
        at={CHIP_YOU}
        side="right"
        emoji="💸"
        label={copy.steps.youLabel}
        value={fill(copy, copy.steps.youValue, STORY_GAIN)}
        accent={LOOK.green}
      />

      {/* Notification lands, coins as the balance rolls, then the two chips. */}
      <Sfx sound="notify" at={NOTIF_IN + 4} />
      <Sfx sound="coins" at={ROLL_FROM + 4} />
      <Sfx sound="pop" at={CHIP_FRIEND} volume={0.8} />
      <Sfx sound="pop" at={CHIP_YOU} volume={0.8} />

      {/* Push notification */}
      <div
        style={{
          position: "absolute",
          left: g.phoneX + g.phoneW / 2 - NOTIF_W / 2,
          width: NOTIF_W,
          top: notifTop,
          translate: `0 ${notifY}px`,
          padding: "26px 30px",
          borderRadius: 44,
          background: "rgba(250, 250, 252, 0.96)",
          boxShadow: "0 30px 60px -12px rgba(0, 20, 80, 0.45)",
          display: "flex",
          gap: 24,
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            flex: "none",
            width: 84,
            height: 84,
            borderRadius: 22,
            background: LOOK.blue,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FrakLogo variant="icon" width={50} color={LOOK.white} />
        </div>
        <div style={{ flex: 1, fontFamily: LOOK.body, color: LOOK.ink }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 22,
              fontWeight: LOOK.semi,
              color: LOOK.inkSoft,
              letterSpacing: 1,
            }}
          >
            <span>{copy.notification.app}</span>
            <span style={{ letterSpacing: 0, fontWeight: LOOK.medium }}>
              {copy.notification.time}
            </span>
          </div>
          <div style={{ fontSize: 36, fontWeight: LOOK.bold, marginTop: 4, letterSpacing: -0.5 }}>
            {fill(copy, copy.notification.title, STORY_GAIN)}
          </div>
          <div style={{ fontSize: 27, lineHeight: 1.25, marginTop: 4, color: LOOK.inkSoft }}>
            {fill(copy, copy.notification.body, STORY_GAIN)}
          </div>
        </div>
      </div>
    </StepStage>
  );
};
